import type { Capability } from '@brightlocal/api-client';
import type { CapabilityKey } from '@brightlocal/shared';

import { captureSentryException } from 'shared/utils';

import { CAPABILITIES_FEATURE_TAG } from '../consts';

/** Value of a capability the plan defines */
type DefinedCapabilityValue = NonNullable<Capability['value']>;

/** Params shared by the capability accessors */
interface GetCapabilityParams {
  /** Capabilities list from the Capabilities API; `null` or `undefined` when the read failed or hasn't loaded */
  capabilities: Capability[] | null | undefined;
  /** Capability to read */
  key: CapabilityKey;
}

interface ReadCapabilityParams<T extends DefinedCapabilityValue> extends GetCapabilityParams {
  /** Whether the value has the shape the accessor returns */
  isExpectedShape: (value: DefinedCapabilityValue) => value is T;
  /** Value returned when the read failed or hasn't loaded, or the capability is missing, undefined by the plan, or malformed */
  safeValue: T;
}

interface WarnAboutCapabilityParams {
  /** The loaded capabilities list the problem is in */
  capabilities: Capability[];
  /** Capability with the problem */
  key: CapabilityKey;
  /** Sentry message; also part of the fingerprint */
  message: string;
  /** The malformed value, when there is one */
  value?: unknown;
}

/**
 * Warnings already sent per capabilities snapshot, as `message:key`. A gate renders many times
 * with the same list from the query cache, so each problem is reported once per snapshot; a
 * refetch with changed data is a new array and is checked again.
 */
const warnedCapabilities = new WeakMap<Capability[], Set<string>>();

/** Captures a warning about a capability that is missing or malformed, once per snapshot and key. */
const warnAboutCapability = ({ capabilities, message, key, value }: WarnAboutCapabilityParams) => {
  const warned = warnedCapabilities.get(capabilities) ?? new Set<string>();
  const warningId = `${message}:${key}`;

  if (warned.has(warningId)) return;

  warned.add(warningId);
  warnedCapabilities.set(capabilities, warned);

  captureSentryException({
    message,
    feature: CAPABILITIES_FEATURE_TAG,
    level: 'warning',
    extra: { key, value },
    fingerprint: [CAPABILITIES_FEATURE_TAG, message, key]
  });
};

/**
 * Reads a capability and returns a safe value when the read failed or hasn't loaded, or the
 * capability is missing, undefined by the plan, or malformed.
 */
const readCapability = <T extends DefinedCapabilityValue>({
  capabilities,
  key,
  isExpectedShape,
  safeValue
}: ReadCapabilityParams<T>) => {
  // No list is a failed or pending read, not a problem with the capability, so nothing is reported.
  if (!capabilities) {
    return safeValue;
  }

  const capability = capabilities.find(({ name }) => name === key);

  if (capability === undefined) {
    warnAboutCapability({ capabilities, message: 'Capability is missing', key });
    return safeValue;
  }

  const { value } = capability;

  // `null` is a valid answer: the plan does not define this capability.
  if (value === null) {
    return safeValue;
  }

  if (!isExpectedShape(value)) {
    warnAboutCapability({ capabilities, message: 'Capability has an unexpected value shape', key, value });
    return safeValue;
  }

  return value;
};

const isOnOff = (value: DefinedCapabilityValue): value is boolean => typeof value === 'boolean';
const isQuota = (value: DefinedCapabilityValue): value is number => typeof value === 'number';
const isList = (value: DefinedCapabilityValue): value is string[] => Array.isArray(value);

/** Whether an on/off capability is enabled; `false` when there is no list, or it is missing or malformed. */
export const isCapabilityEnabled = (params: GetCapabilityParams) =>
  readCapability({ ...params, isExpectedShape: isOnOff, safeValue: false });

/** Reads a numeric capability (a credit or usage limit); `0` when there is no list, or it is missing or malformed. */
export const getQuotaCapability = (params: GetCapabilityParams) =>
  readCapability({ ...params, isExpectedShape: isQuota, safeValue: 0 });

/** Reads a list capability; `[]` when there is no list, or it is missing or malformed. */
export const getListCapability = (params: GetCapabilityParams) =>
  readCapability({ ...params, isExpectedShape: isList, safeValue: [] });
